import React, { useEffect, useMemo, useState } from "react";import api, { API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import {
  Upload,
  Trash2,
  FileText,
  Download,
  User,
  Pencil,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X,
  Search,
  ChevronRight,
} from "lucide-react";
import DropZone from "@/components/DropZone";
import ConfirmDialog from "@/components/ConfirmDialog";
import CategoryManager from "@/components/CategoryManager";
import InvoiceCheckArchive from "@/components/InvoiceCheckArchive";

// Mesi in italiano per selettori "mese di competenza"
const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];
// Regex lato client per auto-estrazione POD / PDR dai titoli file
const POD_RE = /(?<![A-Za-z0-9])(IT\d{3}[EeMm]\d{7,8}[A-Za-z0-9])(?![A-Za-z0-9])/;
const PDR_RE = /(?<!\d)(\d{14})(?!\d)/;
const extractPodPdr = (text, commodity) => {
  if (!text) return "";
  if (commodity === "power") {
    const m = text.match(POD_RE); if (m) return m[1].toUpperCase();
  } else if (commodity === "gas") {
    const m = text.match(PDR_RE); if (m) return m[1];
  }
  const mPod = text.match(POD_RE); if (mPod) return mPod[1].toUpperCase();
  const mPdr = text.match(PDR_RE); if (mPdr) return mPdr[1];
  return "";
};

const StatusIcon = ({ state }) => {
  if (state === "done") return <CheckCircle2 size={14} className="text-emerald-600" />;
  if (state === "error") return <AlertCircle size={14} className="text-red-500" />;
  if (state === "uploading") return <Loader2 size={14} className="text-blue-600 animate-spin" />;
  return <div className="w-2 h-2 rounded-full bg-slate-300" />;
};

export default function AdminCustomReports() {
  const [users, setUsers] = useState([]);  // now: companies list
  const [companyUsers, setCompanyUsers] = useState([]);  // users of the selectedClient (company)
  const [selectedClient, setSelectedClient] = useState("");
  const [reports, setReports] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [files, setFiles] = useState([]);
  // Metadata per-file per il "Controllo Fatture" (mappa filename→meta).
  // Se un file non è nella mappa OR is_invoice=false → viene caricato come report generico.
  const [invoiceMeta, setInvoiceMeta] = useState({});
  // --- BATCH MODE "Controllo Fatture" ---
  // Toggle: quando attivo tutto il batch è un controllo fatture con stesso mese/anno/commodity.
  const now = new Date();
  const [batchInvoiceMode, setBatchInvoiceMode] = useState(false);
  const [batchInvoice, setBatchInvoice] = useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    commodity: "",
  });
  // POD/PDR per singolo file nel batch (filename -> code). Vuoto = auto-estrai.
  const [filePods, setFilePods] = useState({});
  // Lista PdP censiti sull'azienda selezionata (per dropdown)
  const [companyPdps, setCompanyPdps] = useState([]);
  const [titlePrefix, setTitlePrefix] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [sendAsMonthlyZip, setSendAsMonthlyZip] = useState(false);
  // Modale PdP non censiti: aperto quando il bulk-upload ritorna PODs mai visti
  const [unregisteredPdps, setUnregisteredPdps] = useState([]);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  // Destinatari email: "" (non scelto, blocca conferma) | "all" (tutti utenti azienda) | "users" (subset)
  const [emailTargetType, setEmailTargetType] = useState("");
  const [emailClientIds, setEmailClientIds] = useState(new Set()); // subset of company users for email delivery
  const [emailUserSearch, setEmailUserSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({}); // { [name]: 'pending' | 'uploading' | 'done' | 'error' }
  const [editing, setEditing] = useState(null); // report obj
  const [editForm, setEditForm] = useState({ title: "", description: "", category: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [search, setSearch] = useState("");
  const [confirmState, setConfirmState] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set()); // shared across both tables
  const [bulkBusy, setBulkBusy] = useState(false);
  const loadCategories = async () => {
    try {
      const { data } = await api.get("/custom-reports/categories");
      setCategories(data);
    } catch (_e) { /* silent */ }
  };

  const loadUsers = async () => {
    const { data } = await api.get("/companies");
    setUsers(data);
  };
  const loadReportsForClient = async () => {
    if (!selectedClient) {
      setReports([]);
      return;
    }
    const { data } = await api.get("/custom-reports", { params: { company_id: selectedClient } });
    setReports(data);
  };
  const loadAllReports = async () => {
    const { data } = await api.get("/custom-reports");
    setAllReports(data);
  };
  useEffect(() => {
    loadUsers();
    loadAllReports();
    loadCategories();
  }, []);
  useEffect(() => {
    loadReportsForClient();
    // Reset email recipient subset when switching company
    setEmailClientIds(new Set());
    setEmailTargetType("");
    setEmailUserSearch("");
    // Fetch users of this company for the "email recipients" picker
    if (!selectedClient) {
      setCompanyUsers([]);
      setCompanyPdps([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/users");
        setCompanyUsers(
          Array.isArray(data)
            ? data.filter((u) => u.role === "cliente" && u.status === "active" && u.company_id === selectedClient)
            : []
        );
      } catch (e) {
        setCompanyUsers([]);
      }
      // Carica PdP dell'azienda per il dropdown
      try {
        const { data: pdps } = await api.get(`/companies/${selectedClient}/pdps`);
        setCompanyPdps(Array.isArray(pdps) ? pdps : []);
      } catch (_e) {
        setCompanyPdps([]);
      }
    })();
  }, [selectedClient]);

  const upload = async () => {
    if (!selectedClient) return toast.error("Seleziona un'azienda");
    if (!files.length) return toast.error("Seleziona almeno un file PDF");
    if (files.length > 20) return toast.error("Massimo 20 PDF per upload");
    // Validazione batch mode: anno/mese obbligatori
    if (batchInvoiceMode) {
      if (!batchInvoice.year || !batchInvoice.month) {
        return toast.error("In modalità Controllo Fatture devi scegliere mese e anno di competenza");
      }
      if (!batchInvoice.commodity) {
        return toast.error("Seleziona il tipo di controllo (Gas / Power / Dual)");
      }
    }
    setBusy(true);
    setProgress(Object.fromEntries(files.map((f) => [f.name, "uploading"])));
    try {
      const fd = new FormData();
      const titles = [];
      for (const f of files) {
        fd.append("files", f);
        const base = f.name.replace(/\.pdf$/i, "");
        titles.push(titlePrefix.trim() ? `${titlePrefix.trim()} — ${base}` : base);
      }
      fd.append("titles", JSON.stringify(titles));
      fd.append("company_id", selectedClient);
      if (batchInvoiceMode) {
        // Metadati unici per tutto il batch
        fd.append("invoice_check_batch", JSON.stringify({
          year: Number(batchInvoice.year),
          month: Number(batchInvoice.month),
          commodity: batchInvoice.commodity || null,
        }));
        // POD per singolo file (vuoto = auto-estrai lato server dal nome)
        const podsArr = files.map((f) => (filePods[f.name] || "").trim());
        if (podsArr.some((p) => p)) {
          fd.append("pods", JSON.stringify(podsArr));
        }
      } else {
        // Legacy: array per-file (nessun invoice-check in questo path se la mappa è vuota)
        const invMetaArr = files.map((f) => {
          const m = invoiceMeta[f.name];
          if (!m || !m.is_invoice) return null;
          if (!m.year || !m.month) return null;
          return {
            year: Number(m.year),
            month: Number(m.month),
            commodity: m.commodity || null,
            pod: (m.pod || "").trim() || null,
          };
        });
        if (invMetaArr.some((x) => x != null)) {
          fd.append("invoice_check_meta", JSON.stringify(invMetaArr));
        }
      }
      if (description) fd.append("description", description);
      if (category && !batchInvoiceMode) fd.append("category", category);
      if (sendEmail) {
        fd.append("send_email", "true");
        if (emailSubject.trim()) fd.append("email_subject", emailSubject.trim());
        if (emailMessage.trim()) fd.append("email_message", emailMessage.trim());
        // Only forward the subset if admin explicitly picked "users specifici"
        if (emailTargetType === "users" && emailClientIds.size > 0) {
          fd.append("email_client_ids", JSON.stringify(Array.from(emailClientIds)));
        }
        if (batchInvoiceMode && sendAsMonthlyZip) {
          fd.append("send_as_monthly_zip", "true");
        }
      }
      const { data } = await api.post("/custom-reports/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploadedCount = (data?.uploaded || []).length;
      setProgress(Object.fromEntries(files.map((f) => [f.name, "done"])));
      toast.success(
        uploadedCount === 1
          ? `1 report caricato${data?.email_sent ? " · email inviata" : ""}`
          : `${uploadedCount} report caricati${data?.email_sent ? " · 1 email con tutti gli allegati inviata" : ""}`
      );
      // Mostra modale riconciliazione PdP se il server ne ha trovati di non censiti
      const newPdps = Array.isArray(data?.unregistered_pdps) ? data.unregistered_pdps : [];
      if (newPdps.length > 0 && selectedClient) {
        setUnregisteredPdps(newPdps.map((p) => ({ ...p, _checked: true })));
      }
      setFiles([]);
      setInvoiceMeta({});
      setFilePods({});
      setBatchInvoiceMode(false);
      setSendAsMonthlyZip(false);
      setTitlePrefix("");
      setDescription("");
      setCategory("");
      setEmailSubject("");
      setEmailMessage("");
      setEmailTargetType("");
      setEmailClientIds(new Set());
      setEmailUserSearch("");
      loadReportsForClient();
      loadAllReports();
      loadUsers();
      loadCategories();
    } catch (e) {
      setProgress(Object.fromEntries(files.map((f) => [f.name, "error"])));
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally {
      setBusy(false);
    }
  };

  const del = (r, refresh = "both") => {
    setConfirmState({
      title: "Eliminare questo report?",
      message: `"${r.title || r.filename}" verrà rimosso definitivamente.`,
      confirmLabel: "Elimina",
      variant: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/custom-reports/${r.id}`);
          toast.success("Report eliminato");
          if (refresh === "both" || refresh === "client") loadReportsForClient();
          if (refresh === "both" || refresh === "all") loadAllReports();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        }
      },
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllFrom = (list) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      list.forEach((r) => n.add(r.id));
      return n;
    });
  };
  const areAllSelected = (list) =>
    list.length > 0 && list.every((r) => selectedIds.has(r.id));
  const toggleSelectAllFrom = (list) => {
    if (areAllSelected(list)) {
      setSelectedIds((prev) => {
        const n = new Set(prev);
        list.forEach((r) => n.delete(r.id));
        return n;
      });
    } else {
      selectAllFrom(list);
    }
  };

  const bulkDelete = () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setConfirmState({
      title: `Eliminare ${ids.length} report personalizzat${ids.length === 1 ? "o" : "i"}?`,
      message: "L'operazione è irreversibile: i report verranno rimossi dal portale per tutti i clienti destinatari.",
      confirmLabel: `Elimina ${ids.length}`,
      variant: "danger",
      onConfirm: async () => {
        setBulkBusy(true);
        try {
          const { data } = await api.post("/custom-reports/bulk-delete", { ids });
          toast.success(`${data.deleted} report eliminat${data.deleted === 1 ? "o" : "i"}`);
          clearSelection();
          await loadReportsForClient();
          await loadAllReports();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione multipla");
        } finally {
          setBulkBusy(false);
        }
      },
    });
  };

  const download = (id, filename) => {
    const url = `${API}/custom-reports/${id}/download?auth=${encodeURIComponent(getToken() || "")}`;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.download = filename || "report.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const openEdit = (r) => {
    setEditing(r);
    setEditForm({ title: r.title || "", description: r.description || "", category: r.category || "" });
  };
  const closeEdit = () => {
    setEditing(null);
    setEditForm({ title: "", description: "", category: "" });
  };
  const saveEdit = async () => {
    if (!editing) return;
    if (!editForm.title.trim()) return toast.error("Titolo obbligatorio");
    setSavingEdit(true);
    try {
      await api.patch(`/custom-reports/${editing.id}`, {
        title: editForm.title.trim(),
        description: editForm.description ?? "",
        category: editForm.category ?? "",
      });
      toast.success("Report aggiornato");
      closeEdit();
      loadReportsForClient();
      loadAllReports();
      loadCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setSavingEdit(false);
    }
  };

  const selClient = users.find((u) => u.id === selectedClient);

  const clientLabel = (r) =>
    r.client_company_name || r.client_full_name || r.client_email || r.client_id;

  const [filterCompanyId, setFilterCompanyId] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  // Nuovi filtri invoice-check sull'archivio globale
  const [filterCommodity, setFilterCommodity] = useState("");  // "gas" | "power" | "both"
  const [filterYear, setFilterYear] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  // Toggle: raggruppa per cliente (vista compatta) o lista piatta
  const [groupByClient, setGroupByClient] = useState(true);
  const [expandedCompanies, setExpandedCompanies] = useState({});

  const filteredAll = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allReports.filter((r) => {
      if (q) {
        const match = [r.title, r.description, r.filename, clientLabel(r), r.client_email, r.pod]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
        if (!match) return false;
      }
      if (filterCompanyId && r.company_id !== filterCompanyId && r.client_id !== filterCompanyId) return false;
      if (filterCategory && (r.category || "") !== filterCategory) return false;
      if (filterCommodity && r.commodity !== filterCommodity) return false;
      if (filterYear && Number(r.billing_year) !== Number(filterYear)) return false;
      if (filterMonth && Number(r.billing_month) !== Number(filterMonth)) return false;
      if (filterFrom) {
        const from = new Date(filterFrom).getTime();
        const created = new Date(r.created_at || 0).getTime();
        if (created < from) return false;
      }
      if (filterTo) {
        const to = new Date(filterTo + "T23:59:59").getTime();
        const created = new Date(r.created_at || 0).getTime();
        if (created > to) return false;
      }
      return true;
    });
  }, [allReports, search, filterCompanyId, filterCategory, filterCommodity, filterYear, filterMonth, filterFrom, filterTo]);

  // Aggregazione per azienda per la "vista compatta"
  const groupedByCompany = useMemo(() => {
    const groups = new Map();
    for (const r of filteredAll) {
      const key = r.company_id || r.client_id || "orfani";
      if (!groups.has(key)) {
        groups.set(key, { company_id: key, company_name: r.company_name || r.client_name || "— senza azienda —", reports: [] });
      }
      groups.get(key).reports.push(r);
    }
    return Array.from(groups.values()).sort((a, b) => a.company_name.localeCompare(b.company_name));
  }, [filteredAll]);

  // Lookup PdP (POD/PDR → { label, address }) per azienda, usando le aziende caricate dallo stato `users`.
  const pdpsByCompany = useMemo(() => {
    const out = {};
    for (const c of users || []) {
      const map = {};
      for (const p of (c.pdps || [])) {
        const code = (p.code || "").toUpperCase();
        if (code) map[code] = { label: p.label || null, address: p.address || null };
      }
      out[c.id] = map;
    }
    return out;
  }, [users]);

  // Anni disponibili per il filtro
  const availableYears = useMemo(() => {
    const s = new Set();
    for (const r of allReports) if (r.billing_year) s.add(r.billing_year);
    return Array.from(s).sort((a, b) => b - a);
  }, [allReports]);

  const anyFilterActive = !!(search || filterCompanyId || filterCategory || filterCommodity || filterYear || filterMonth || filterFrom || filterTo);
  const clearFilters = () => {
    setSearch("");
    setFilterCompanyId("");
    setFilterCategory("");
    setFilterCommodity("");
    setFilterYear("");
    setFilterMonth("");
    setFilterFrom("");
    setFilterTo("");
    setFilterFrom("");
    setFilterTo("");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
          Gestione Report Personalizzati
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Carica PDF specifici per singolo cliente. Il cliente vedrà la sezione &quot;Report Personalizzati&quot; solo se abilitato.
        </p>
      </div>

      {/* Categorie management */}
      <CategoryManager
        endpoint="/custom-reports/categories"
        title="Categorie report personalizzati"
        subtitle="Le categorie aiutano il cliente a filtrare i propri report personalizzati."
        confirmDelete={setConfirmState}
        onChange={loadCategories}
      />

      {/* Upload card */}
      <div className="card-elevated p-5">
        <div className="font-display font-semibold mb-3 flex items-center gap-2">
          <Upload size={18} className="text-blue-600" /> Carica nuovi report
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
              Azienda destinataria
            </label>
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500"
              data-testid="cr-select-client"
            >
              <option value="">— Seleziona un&apos;azienda —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id} disabled={u.is_demo_company}>
                  {u.is_demo_company ? "🔒 " : ""}{u.name} · {u.users_count} utent{u.users_count !== 1 ? "i" : "e"}
                  {u.is_demo_company
                    ? " — azienda demo, non consentito"
                    : (u.custom_reports_enabled ? " ✓" : "")}
                </option>
              ))}
            </select>
            {selClient?.is_demo_company && (
              <div className="mt-1 text-xs text-red-600 flex items-start gap-1" data-testid="cr-demo-blocked-warning">
                <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                <span>
                  Non è possibile caricare report personalizzati sull&apos;azienda demo:
                  sarebbero visibili a tutti i prospect. Seleziona un&apos;azienda cliente reale.
                </span>
              </div>
            )}
            {selClient && !selClient.is_demo_company && !selClient.custom_reports_enabled && (
              <div className="mt-1 text-xs text-amber-600">
                La sezione verrà abilitata automaticamente al primo upload.
              </div>
            )}
          </div>
          <div>
            <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
              Prefisso titolo (opzionale)
            </label>
            <input
              value={titlePrefix}
              onChange={(e) => setTitlePrefix(e.target.value)}
              placeholder="Es. Analisi Q4 2026 — (il nome file viene aggiunto)"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
              data-testid="cr-title-prefix"
            />
            <div className="mt-1 text-[11px] text-slate-400">
              Vuoto = usa il nome del file come titolo.
            </div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
              Descrizione (opzionale, applicata a tutti)
            </label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descrizione visibile al cliente"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
              Categoria (opzionale)
            </label>
            <input
              list="cr-cat-list"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Es. Fatture, Analisi, Consuntivi…"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
              data-testid="cr-category-input"
            />
            <datalist id="cr-cat-list">
              {categories.map((c) => <option key={c.name} value={c.name} />)}
            </datalist>
          </div>
        </div>
        <div className="mt-3">
          <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold block mb-1">
            File PDF (multipli)
          </label>
          <DropZone
            files={files}
            multiple
            onChange={setFiles}
            accept=".pdf,application/pdf"
            hint="Trascina qui i PDF oppure clicca per selezionarli"
            testId="cr-file"
          />
          <p className="text-xs text-slate-500 mt-1">
            {sendEmail
              ? "Se carichi più file, verrà inviata UNA sola email con tutti gli allegati."
              : "Puoi caricare più file contemporaneamente (max 20)."}
          </p>
        </div>

        {/* --- Toggle Batch Mode "Controllo Fatture" --- */}
        {files.length > 0 && (
          <div className="mt-4 border border-sky-200 rounded-xl bg-gradient-to-br from-sky-50/60 to-white p-4" data-testid="cr-batch-mode-panel">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={batchInvoiceMode}
                onChange={(e) => setBatchInvoiceMode(e.target.checked)}
                data-testid="cr-batch-mode-toggle"
              />
              <span className="text-sm font-semibold text-slate-800">
                Modalità Controllo Fatture
              </span>
              <span className="text-[11px] text-slate-500 ml-2">
                — stesso mese/anno/tipo per tutti i {files.length} PDF
              </span>
            </label>
            {batchInvoiceMode && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold block mb-1">Tipo *</label>
                    <select
                      value={batchInvoice.commodity}
                      onChange={(e) => {
                        const commodity = e.target.value;
                        setBatchInvoice((b) => ({ ...b, commodity }));
                        // Auto-estrai POD/PDR dai nomi file quando cambia la commodity
                        setFilePods((prev) => {
                          const next = { ...prev };
                          for (const f of files) {
                            if (!next[f.name]) {
                              const guess = extractPodPdr(f.name.replace(/\.pdf$/i, ""), commodity);
                              if (guess) next[f.name] = guess;
                            }
                          }
                          return next;
                        });
                      }}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                      data-testid="cr-batch-commodity"
                    >
                      <option value="">— seleziona —</option>
                      <option value="gas">Gas</option>
                      <option value="power">Power</option>
                      <option value="both">Dual (Gas + Power)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold block mb-1">Mese di competenza *</label>
                    <select
                      value={batchInvoice.month}
                      onChange={(e) => setBatchInvoice((b) => ({ ...b, month: Number(e.target.value) }))}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                      data-testid="cr-batch-month"
                    >
                      {MONTHS_IT.map((lbl, i) => (
                        <option key={i} value={i + 1}>{lbl}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold block mb-1">Anno *</label>
                    <input
                      type="number" min="2020" max="2100"
                      value={batchInvoice.year}
                      onChange={(e) => setBatchInvoice((b) => ({ ...b, year: Number(e.target.value) }))}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                      data-testid="cr-batch-year"
                    />
                  </div>
                </div>
                {/* Riga POD per singolo file */}
                <div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold mb-1">
                    PDP (POD / PDR) per file
                    <span className="text-[10px] normal-case text-slate-400 ml-1">
                      — auto-estratto dal nome, modificabile. {companyPdps.length > 0 ? "Dropdown dei PDP censiti disponibili." : ""}
                    </span>
                  </div>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto">
                    {files.map((f) => {
                      const auto = filePods[f.name] ?? extractPodPdr(f.name.replace(/\.pdf$/i, ""), batchInvoice.commodity);
                      // Dropdown filtrato per commodity (power → POD, gas → PDR, both → tutti)
                      const dropOpts = companyPdps.filter((p) => {
                        if (batchInvoice.commodity === "power") return p.type === "power";
                        if (batchInvoice.commodity === "gas") return p.type === "gas";
                        return true;
                      });
                      return (
                        <div key={f.name} className="flex items-center gap-2 text-xs">
                          <span className="font-mono text-slate-600 truncate flex-shrink min-w-0" style={{maxWidth: 240}} title={f.name}>{f.name}</span>
                          <input
                            type="text"
                            placeholder={batchInvoice.commodity === "gas" ? "PDR 14 cifre" : "POD IT001E..."}
                            value={auto || ""}
                            onChange={(e) => setFilePods((p) => ({ ...p, [f.name]: e.target.value }))}
                            className="flex-1 px-2 py-1 rounded border border-slate-200 text-xs font-mono outline-none focus:border-blue-500"
                            data-testid={`cr-batch-pod-${f.name}`}
                          />
                          {dropOpts.length > 0 && (
                            <select
                              value=""
                              onChange={(e) => {
                                if (e.target.value) setFilePods((p) => ({ ...p, [f.name]: e.target.value }));
                              }}
                              className="px-2 py-1 rounded border border-slate-200 text-[11px] text-slate-500 outline-none focus:border-blue-500"
                              title="Seleziona dall'elenco PDP dell'azienda"
                              data-testid={`cr-batch-pod-picker-${f.name}`}
                            >
                              <option value="">PDP censiti…</option>
                              {dropOpts.map((p) => (
                                <option key={p.id} value={p.code}>{p.code}{p.label ? ` — ${p.label}` : ""}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* Opzione extra: invia come 1 ZIP mensile invece di N allegati */}
                {sendEmail && (
                  <label className="flex items-start gap-2 text-xs bg-emerald-50/50 border border-emerald-200 rounded-lg px-3 py-2 cursor-pointer" data-testid="cr-batch-zip-label">
                    <input
                      type="checkbox"
                      checked={sendAsMonthlyZip}
                      onChange={(e) => setSendAsMonthlyZip(e.target.checked)}
                      className="mt-0.5"
                      data-testid="cr-batch-zip-toggle"
                    />
                    <span>
                      <span className="font-semibold text-emerald-800">Invia come ZIP mensile unico</span>
                      <span className="text-slate-500"> — una sola email con un file ZIP che contiene tutti i {files.length} PDF (anziché {files.length} allegati separati). Consigliato per 10+ file.</span>
                    </span>
                  </label>
                )}
              </div>
            )}
          </div>
        )}

        {files.length > 0 && !busy && !batchInvoiceMode && (
          <div className="mt-4 border border-purple-100 rounded-xl bg-gradient-to-br from-purple-50/40 to-white p-4" data-testid="cr-invoice-check-panel">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={16} className="text-purple-600" />
              <div className="text-sm font-semibold text-slate-800">
                Report di Controllo Fatture (per-file, opzionale)
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mb-3">
              Attiva la spunta sui file che sono controlli fatture: verranno raggruppati automaticamente
              nella categoria <b>“Controllo Fatture”</b> e nel riquadro dedicato dell'area cliente,
              filtrabili per mese di competenza.
            </p>
            <div className="space-y-2">
              {files.map((f) => {
                const meta = invoiceMeta[f.name] || {};
                const setMeta = (patch) =>
                  setInvoiceMeta((prev) => ({ ...prev, [f.name]: { ...(prev[f.name] || {}), ...patch } }));
                const isInv = !!meta.is_invoice;
                return (
                  <div key={f.name} className={`rounded-lg border ${isInv ? "border-purple-300 bg-white shadow-sm" : "border-slate-200 bg-white/60"} p-2.5`}>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isInv}
                        onChange={(e) => setMeta({ is_invoice: e.target.checked })}
                        data-testid={`cr-invoice-check-toggle-${f.name}`}
                      />
                      <FileText size={13} className="text-slate-400" />
                      <span className="font-medium text-slate-700 truncate flex-1">{f.name}</span>
                      {isInv && (
                        <span className="badge-neutral !bg-purple-100 !text-purple-700 text-[10px]">
                          Controllo Fattura
                        </span>
                      )}
                    </label>
                    {isInv && (
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-4 gap-2 pl-6">
                        <select
                          value={meta.month || ""}
                          onChange={(e) => setMeta({ month: e.target.value })}
                          className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-purple-500"
                          data-testid={`cr-invoice-month-${f.name}`}
                        >
                          <option value="">Mese di competenza *</option>
                          {["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"].map((n, i) => (
                            <option key={i+1} value={i+1}>{n}</option>
                          ))}
                        </select>
                        <select
                          value={meta.year || ""}
                          onChange={(e) => setMeta({ year: e.target.value })}
                          className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-purple-500"
                          data-testid={`cr-invoice-year-${f.name}`}
                        >
                          <option value="">Anno *</option>
                          {Array.from({ length: 6 }).map((_, i) => {
                            const y = new Date().getFullYear() - i;
                            return <option key={y} value={y}>{y}</option>;
                          })}
                        </select>
                        <select
                          value={meta.commodity || ""}
                          onChange={(e) => setMeta({ commodity: e.target.value })}
                          className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-purple-500"
                          data-testid={`cr-invoice-commodity-${f.name}`}
                        >
                          <option value="">Commodity (opz.)</option>
                          <option value="gas">Gas</option>
                          <option value="power">Power</option>
                          <option value="both">Gas + Power</option>
                        </select>
                        <input
                          type="text"
                          value={meta.pod || ""}
                          onChange={(e) => setMeta({ pod: e.target.value })}
                          placeholder="POD/PDR (opz.)"
                          className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-purple-500"
                          data-testid={`cr-invoice-pod-${f.name}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {busy && files.length > 0 && (
          <div className="mt-3 space-y-1" data-testid="cr-upload-progress">
            {files.map((f) => (
              <div key={f.name} className="flex items-center gap-2 text-xs text-slate-600">
                <StatusIcon state={progress[f.name] || "pending"} />
                <span className="truncate">{f.name}</span>
              </div>
            ))}
          </div>
        )}
        <label
          className={`mt-4 flex items-start gap-2 text-sm cursor-pointer rounded-lg px-3 py-2 -mx-1 ${
            sendEmail ? "bg-amber-50 border border-amber-200" : "border border-transparent"
          }`}
          data-testid="cr-send-email-label"
        >
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => {
              setSendEmail(e.target.checked);
              if (!e.target.checked) {
                setEmailTargetType("");
                setEmailClientIds(new Set());
              }
            }}
            className="mt-0.5"
            data-testid="cr-send-email"
            disabled={busy}
          />
          <span className="flex-1">
            <span className="text-slate-700">Invia anche <b>email</b> con il report allegato</span>
            <span className="block text-xs text-slate-500 mt-0.5">
              Il template Adybo aggiunge header, logo, disclaimer &quot;Casella non abilitata&quot; e link al portale.
              Rispetta la preferenza &quot;Notifiche&quot; del Profilo di ciascun utente.
            </span>
          </span>
        </label>
        {sendEmail && (
          <div className="mt-3 space-y-3 pl-6" data-testid="cr-email-editor">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Oggetto email</label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Oggetto (lascia vuoto per usare il default: 'Nuovo report personalizzato')"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:border-blue-500 outline-none"
                disabled={busy}
                data-testid="cr-email-subject"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Messaggio email</label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Corpo dell'email (lascia vuoto per usare il default: il titolo del report). Verrà inserito nel template branded Adybo con logo e CTA."
                rows={4}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:border-blue-500 outline-none resize-y"
                disabled={busy}
                data-testid="cr-email-message"
              />
              <div className="text-[10px] text-slate-400 mt-1">
                Uguale per tutti i report caricati insieme. Il template Adybo aggiunge header, logo e pulsante &quot;Apri sul portale&quot;.
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">
                Destinatari email <span className="text-red-500">*</span>
              </label>
              <select
                value={emailTargetType}
                onChange={(e) => {
                  setEmailTargetType(e.target.value);
                  if (e.target.value !== "users") {
                    setEmailClientIds(new Set());
                    setEmailUserSearch("");
                  }
                }}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
                disabled={busy}
                data-testid="cr-email-target-type"
              >
                <option value="">— Scegli destinatari —</option>
                <option value="all">Tutti gli utenti attivi dell&apos;azienda ({companyUsers.length})</option>
                <option value="users">Solo utenti specifici…</option>
              </select>
              {!emailTargetType && (
                <div className="mt-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Seleziona esplicitamente i destinatari per evitare di inviare l&apos;email a tutta l&apos;azienda per errore.
                </div>
              )}
              {emailTargetType === "users" && (
                <div className="mt-2">
                  {companyUsers.length === 0 ? (
                    <div className="text-xs text-slate-500 italic px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                      Nessun utente attivo per questa azienda.
                    </div>
                  ) : (
                    <>
                      <div className="relative mb-1">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          value={emailUserSearch}
                          onChange={(e) => setEmailUserSearch(e.target.value)}
                          placeholder="Cerca per nome o email…"
                          className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-slate-200 outline-none focus:border-blue-500"
                          data-testid="cr-email-user-search"
                          disabled={busy}
                        />
                      </div>
                      <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="cr-email-recipients">
                        {companyUsers
                          .filter((u) => {
                            const q = emailUserSearch.trim().toLowerCase();
                            if (!q) return true;
                            return (u.full_name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                          })
                          .map((u) => (
                            <label
                              key={u.id}
                              className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50"
                              data-testid={`cr-email-recipient-${u.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={emailClientIds.has(u.id)}
                                onChange={() => {
                                  setEmailClientIds((prev) => {
                                    const n = new Set(prev);
                                    if (n.has(u.id)) n.delete(u.id);
                                    else n.add(u.id);
                                    return n;
                                  });
                                }}
                                disabled={busy}
                              />
                              <span className="flex-1 text-slate-800">{u.full_name || u.email}</span>
                              <span className="text-[10px] text-slate-400">{u.email}</span>
                              {u.email_enabled === false && (
                                <span className="text-[9px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded px-1">EMAIL OFF</span>
                              )}
                            </label>
                          ))}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">
                        {emailClientIds.size === 0
                          ? "Seleziona almeno un utente."
                          : `${emailClientIds.size} utent${emailClientIds.size === 1 ? "e selezionato" : "i selezionati"}. Il report resta visibile sul portale a tutti gli utenti dell'azienda.`}
                      </div>
                    </>
                  )}
                </div>
              )}
              {emailTargetType === "all" && (
                <div className="text-[10px] text-slate-500 mt-1">
                  L&apos;email arriverà a tutti gli {companyUsers.length} utenti attivi dell&apos;azienda. Il report resta visibile sul portale a tutti loro.
                </div>
              )}
            </div>
          </div>
        )}
        <button
          onClick={upload}
          disabled={
            busy ||
            !selectedClient ||
            !files.length ||
            (sendEmail && !emailTargetType) ||
            (sendEmail && emailTargetType === "users" && emailClientIds.size === 0)
          }
          className="btn-brand flex items-center gap-2 mt-3 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="cr-upload-btn"
        >
          <Upload size={16} /> {busy ? "Caricamento…" : `Carica ${files.length || ""} report`}
        </button>
      </div>

      {/* Reports for selected client */}
      {selectedClient && (
        <div className="card-elevated overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 font-display font-semibold text-slate-700 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <User size={16} className="text-blue-600" />
              Report per <span className="text-blue-700">{selClient?.name || "—"}</span>
              <span className="text-xs font-normal text-slate-400">({reports.length})</span>
            </div>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2" data-testid="cr-client-bulk-bar">
                <span className="text-xs font-normal text-slate-500">
                  {selectedIds.size} selezionat{selectedIds.size === 1 ? "o" : "i"}
                </span>
                <button
                  onClick={clearSelection}
                  className="text-xs font-normal text-slate-500 hover:text-slate-700 px-2 py-1 rounded border border-slate-200"
                  data-testid="cr-client-clear-selection"
                >
                  Deseleziona
                </button>
                <button
                  onClick={bulkDelete}
                  disabled={bulkBusy}
                  className="text-xs font-semibold text-white bg-red-600 hover:bg-red-700 px-3 py-1 rounded flex items-center gap-1 disabled:opacity-50"
                  data-testid="cr-client-bulk-delete"
                >
                  <Trash2 size={13} /> Elimina {selectedIds.size}
                </button>
              </div>
            )}
          </div>
          {reports.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">Nessun report caricato per questo cliente.</div>
          ) : (
            <>
              {/* Archivio Controllo Fatture (visibile se almeno un report invoice-check) */}
              {(() => {
                const invoiceOnly = reports.filter((r) => r.is_invoice_check);
                if (!invoiceOnly.length) return null;
                return (
                  <div className="p-4 border-b border-slate-100">
                    <InvoiceCheckArchive
                      reports={invoiceOnly}
                      scope="admin"
                      companyIdForZip={selectedClient}
                      companyName={selClient?.name || null}
                      pdpLabels={Object.fromEntries(
                        companyPdps.filter((p) => p.label).map((p) => [(p.code || "").toUpperCase(), p.label])
                      )}
                      pdpAddresses={Object.fromEntries(
                        companyPdps.filter((p) => p.address).map((p) => [(p.code || "").toUpperCase(), p.address])
                      )}
                    />
                  </div>
                );
              })()}
              <table className="data-table w-full">
              <thead>
                <tr>
                  <th className="w-8">
                    <input
                      type="checkbox"
                      checked={areAllSelected(reports)}
                      onChange={() => toggleSelectAllFrom(reports)}
                      data-testid="cr-client-select-all"
                    />
                  </th>
                  <th>Titolo</th>
                  <th>Descrizione</th>
                  <th>File</th>
                  <th>Caricato</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id} data-testid={`cr-row-${r.id}`} className={selectedIds.has(r.id) ? "bg-blue-50/50" : ""}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        data-testid={`cr-select-${r.id}`}
                      />
                    </td>
                    <td className="!font-sans font-semibold">
                      <FileText size={13} className="inline mr-1 text-blue-600" /> {r.title}
                    </td>
                    <td className="!font-sans text-slate-600">{r.description || "—"}</td>
                    <td className="!font-sans text-xs text-slate-500">
                      {r.filename} · {(r.size / 1024).toFixed(0)}KB
                    </td>
                    <td className="text-xs">{fmtDateTime(r.created_at)}</td>
                    <td className="flex items-center gap-1">
                      <button
                        onClick={() => download(r.id, r.filename)}
                        className="text-blue-600 hover:text-blue-700 p-1"
                        title="Scarica"
                        data-testid={`cr-download-${r.id}`}
                      >
                        <Download size={16} />
                      </button>
                      <button
                        onClick={() => openEdit(r)}
                        className="text-slate-500 hover:text-slate-700 p-1"
                        title="Modifica"
                        data-testid={`cr-edit-${r.id}`}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => del(r, "both")}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Elimina"
                        data-testid={`cr-delete-${r.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
        </div>
      )}

      {/* Global archive: all custom reports across clients */}
      <div className="card-elevated overflow-hidden" data-testid="cr-global-archive">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="font-display font-semibold text-slate-700 flex items-center gap-2">
            <FileText size={16} className="text-blue-600" />
            Archivio completo report personalizzati
            <span className="text-xs font-normal text-slate-400">({filteredAll.length}/{allReports.length})</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2" data-testid="cr-archive-bulk-bar">
                <span className="text-xs font-normal text-slate-500">
                  {selectedIds.size} selezionat{selectedIds.size === 1 ? "o" : "i"}
                </span>
                <button
                  onClick={clearSelection}
                  className="text-xs font-normal text-slate-500 hover:text-slate-700 px-2 py-1 rounded border border-slate-200"
                  data-testid="cr-archive-clear-selection"
                >
                  Deseleziona
                </button>
                <button
                  onClick={bulkDelete}
                  disabled={bulkBusy}
                  className="text-xs font-semibold text-white bg-red-600 hover:bg-red-700 px-3 py-1 rounded flex items-center gap-1 disabled:opacity-50"
                  data-testid="cr-archive-bulk-delete"
                >
                  <Trash2 size={13} /> Elimina {selectedIds.size}
                </button>
              </div>
            )}
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per cliente, titolo, file…"
                className="pl-8 pr-3 py-1.5 text-sm rounded-lg border border-slate-200 outline-none focus:border-blue-500 w-64"
                data-testid="cr-archive-search"
              />
            </div>
          </div>
        </div>
        {/* Advanced filters row */}
        <div className="px-5 py-3 bg-slate-50/50 border-b border-slate-100 flex items-center gap-3 flex-wrap" data-testid="cr-archive-filters">
          <select
            value={filterCompanyId}
            onChange={(e) => setFilterCompanyId(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
            data-testid="cr-filter-company"
          >
            <option value="">Tutte le aziende</option>
            {users.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
            data-testid="cr-filter-category"
          >
            <option value="">Tutte le categorie</option>
            {categories.map((c) => (
              <option key={c.name} value={c.name}>{c.name}{typeof c.count === "number" ? ` (${c.count})` : ""}</option>
            ))}
          </select>
          {/* Nuovi filtri invoice-check (tipo / anno / mese) */}
          <select
            value={filterCommodity}
            onChange={(e) => setFilterCommodity(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
            data-testid="cr-filter-commodity"
          >
            <option value="">Tutti i tipi</option>
            <option value="gas">Gas</option>
            <option value="power">Power</option>
            <option value="both">Dual</option>
          </select>
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
            data-testid="cr-filter-year"
          >
            <option value="">Tutti gli anni</option>
            {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
            data-testid="cr-filter-month"
          >
            <option value="">Tutti i mesi</option>
            {MONTHS_IT.map((lbl, i) => <option key={i} value={i + 1}>{lbl}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 bg-white border border-slate-200 rounded-lg px-2 py-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={groupByClient}
              onChange={(e) => setGroupByClient(e.target.checked)}
              data-testid="cr-group-by-client"
            />
            Raggruppa per cliente
          </label>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            Dal
            <input
              type="date"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
              className="px-2 py-1 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
              data-testid="cr-filter-from"
            />
            al
            <input
              type="date"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
              className="px-2 py-1 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
              data-testid="cr-filter-to"
            />
          </div>
          {anyFilterActive && (
            <button
              onClick={clearFilters}
              className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg bg-white border border-slate-200"
              data-testid="cr-filter-clear"
            >
              Azzera filtri
            </button>
          )}
        </div>
        {filteredAll.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            {allReports.length === 0
              ? "Nessun report personalizzato caricato."
              : "Nessun risultato per la ricerca."}
          </div>
        ) : groupByClient ? (
          /* Vista COMPATTA: aggregata per cliente (accordion espandibile) */
          <div className="divide-y divide-slate-100" data-testid="cr-grouped-view">
            {groupedByCompany.map((g) => {
              const expanded = !!expandedCompanies[g.company_id];
              const invCount = g.reports.filter((r) => r.is_invoice_check).length;
              return (
                <div key={g.company_id} data-testid={`cr-group-${g.company_id}`}>
                  <button
                    onClick={() => setExpandedCompanies((s) => ({ ...s, [g.company_id]: !expanded }))}
                    className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50/60 text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <ChevronRight
                        size={16}
                        className={`text-slate-400 flex-shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`}
                      />
                      <span className="font-semibold text-slate-800 truncate">{g.company_name}</span>
                      <span className="text-xs text-slate-500 flex-shrink-0">
                        {g.reports.length} report{g.reports.length === 1 ? "" : "i"}
                        {invCount > 0 && <> · <span className="text-sky-600 font-medium">{invCount} controllo{invCount === 1 ? "" : " fatture"}</span></>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedClient(g.company_id);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-500 hover:bg-white"
                      >
                        Apri cliente
                      </button>
                    </div>
                  </button>
                  {expanded && (
                    <div className="px-5 pb-4 pt-1 bg-slate-50/30">
                      <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                            <tr>
                              <th className="text-left px-3 py-1.5 font-semibold">Titolo</th>
                              <th className="text-left px-3 py-1.5 font-semibold w-20">Tipo</th>
                              <th className="text-left px-3 py-1.5 font-semibold w-24">Periodo</th>
                              <th className="text-left px-3 py-1.5 font-semibold">PDP · Indirizzo</th>
                              <th className="text-left px-3 py-1.5 font-semibold w-24">Caricato</th>
                              <th className="text-right px-3 py-1.5 font-semibold w-20"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.reports.map((r) => {
                              const pdpInfo = (r.pod && pdpsByCompany[g.company_id])
                                ? pdpsByCompany[g.company_id][(r.pod || "").toUpperCase()]
                                : null;
                              const addr = (pdpInfo?.address) || r.address || null;
                              return (
                              <tr key={r.id} className="border-t border-slate-100">
                                <td className="px-3 py-1.5 text-slate-700 truncate" style={{maxWidth: 240}}>{r.title}</td>
                                <td className="px-3 py-1.5">
                                  {r.commodity && (
                                    <span className={`inline-flex items-center text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded-full ${
                                      r.commodity === "gas" ? "bg-orange-100 text-orange-700" :
                                      r.commodity === "power" ? "bg-blue-100 text-blue-700" :
                                      "bg-purple-100 text-purple-700"
                                    }`}>{r.commodity === "both" ? "Dual" : r.commodity}</span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 text-slate-600 text-xs">
                                  {r.billing_year ? `${MONTHS_IT[r.billing_month - 1]?.slice(0,3) || ""} ${r.billing_year}` : "—"}
                                </td>
                                <td className="px-3 py-1.5 text-[11px] text-slate-500" style={{maxWidth: 220}}>
                                  {r.pod ? (
                                    <div className="flex flex-col gap-0.5">
                                      <div className="flex items-center gap-1 flex-wrap">
                                        <span className="font-mono text-slate-700">{r.pod}</span>
                                        {pdpInfo?.label && (
                                          <span className="inline-flex items-center text-[10px] font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-1.5 py-0.5">
                                            {pdpInfo.label}
                                          </span>
                                        )}
                                      </div>
                                      {addr && (
                                        <span className="text-[10px] text-slate-400 truncate" title={addr}>{addr}</span>
                                      )}
                                    </div>
                                  ) : "—"}
                                </td>
                                <td className="px-3 py-1.5 text-slate-400 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                                <td className="px-3 py-1.5 text-right">
                                  <a
                                    href={`${API}/custom-reports/${r.id}/download?auth=${encodeURIComponent(getToken() || "")}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                                  >
                                    <Download size={11} /> PDF
                                  </a>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <table className="data-table w-full">
            <thead>
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    checked={areAllSelected(filteredAll)}
                    onChange={() => toggleSelectAllFrom(filteredAll)}
                    data-testid="cr-archive-select-all"
                  />
                </th>
                <th>Cliente</th>
                <th>Titolo</th>
                <th>Descrizione</th>
                <th>File</th>
                <th>Caricato</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredAll.map((r) => (
                <tr key={r.id} data-testid={`cr-archive-row-${r.id}`} className={selectedIds.has(r.id) ? "bg-blue-50/50" : ""}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(r.id)}
                      onChange={() => toggleSelect(r.id)}
                      data-testid={`cr-archive-select-${r.id}`}
                    />
                  </td>
                  <td className="!font-sans">
                    <div className="font-semibold text-slate-800 text-xs">{clientLabel(r)}</div>
                    {r.client_email && (
                      <div className="text-[11px] text-slate-400">{r.client_email}</div>
                    )}
                  </td>
                  <td className="!font-sans font-semibold">
                    <FileText size={13} className="inline mr-1 text-blue-600" /> {r.title}
                  </td>
                  <td className="!font-sans text-slate-600">{r.description || "—"}</td>
                  <td className="!font-sans text-xs text-slate-500">
                    {r.filename} · {(r.size / 1024).toFixed(0)}KB
                  </td>
                  <td className="text-xs">{fmtDateTime(r.created_at)}</td>
                  <td className="flex items-center gap-1">
                    <button
                      onClick={() => download(r.id, r.filename)}
                      className="text-blue-600 hover:text-blue-700 p-1"
                      title="Scarica"
                      data-testid={`cr-archive-download-${r.id}`}
                    >
                      <Download size={16} />
                    </button>
                    <button
                      onClick={() => openEdit(r)}
                      className="text-slate-500 hover:text-slate-700 p-1"
                      title="Modifica"
                      data-testid={`cr-archive-edit-${r.id}`}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => del(r, "all")}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Elimina"
                      data-testid={`cr-archive-delete-${r.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeEdit}
          data-testid="cr-edit-modal"
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-xl font-semibold text-slate-900">
                  Modifica report
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">{editing.filename}</p>
              </div>
              <button
                onClick={closeEdit}
                className="text-slate-400 hover:text-slate-600 p-1"
                data-testid="cr-edit-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
                  Titolo
                </label>
                <input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                  data-testid="cr-edit-title"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
                  Descrizione
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500 resize-none"
                  data-testid="cr-edit-description"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
                  Categoria
                </label>
                <input
                  list="cr-cat-list-edit"
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  placeholder="Nessuna categoria"
                  className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                  data-testid="cr-edit-category"
                />
                <datalist id="cr-cat-list-edit">
                  {categories.map((c) => <option key={c.name} value={c.name} />)}
                </datalist>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={closeEdit}
                className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
                data-testid="cr-edit-cancel"
              >
                Annulla
              </button>
              <button
                onClick={saveEdit}
                disabled={savingEdit}
                className="btn-brand disabled:opacity-40"
                data-testid="cr-edit-save"
              >
                {savingEdit ? "Salvataggio…" : "Salva modifiche"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />

      {/* --- Modale Riconciliazione PdP: PDP trovati nei PDF ma non censiti --- */}
      {unregisteredPdps.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="unregistered-pdps-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">!</span>
                Nuovi PdP rilevati
              </h3>
              <button
                onClick={() => setUnregisteredPdps([])}
                className="text-slate-400 hover:text-slate-600"
                data-testid="unregistered-pdps-close"
              >✕</button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600">
                Nei PDF appena caricati sono stati trovati <b>{unregisteredPdps.length}</b> codici POD/PDR non ancora censiti nell'elenco PdP di <b>{selClient?.name}</b>.
                Spunta quelli che vuoi aggiungere all'anagrafica dell'azienda:
              </p>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-1.5 w-10 text-left"></th>
                      <th className="px-3 py-1.5 w-16 text-left font-semibold">Tipo</th>
                      <th className="px-3 py-1.5 text-left font-semibold">Codice</th>
                      <th className="px-3 py-1.5 text-left font-semibold">Indirizzo fornitura</th>
                      <th className="px-3 py-1.5 text-left font-semibold">Dal file</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unregisteredPdps.map((p, idx) => (
                      <tr key={p.code + idx} className="border-t border-slate-100" data-testid={`unregistered-pdp-row-${p.code}`}>
                        <td className="px-3 py-1.5">
                          <input
                            type="checkbox"
                            checked={p._checked}
                            onChange={(e) => setUnregisteredPdps((arr) => arr.map((x, i) => i === idx ? { ...x, _checked: e.target.checked } : x))}
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <select
                            value={p.type}
                            onChange={(e) => setUnregisteredPdps((arr) => arr.map((x, i) => i === idx ? { ...x, type: e.target.value } : x))}
                            className="px-1.5 py-0.5 rounded border border-slate-200 text-xs"
                          >
                            <option value="power">Power</option>
                            <option value="gas">Gas</option>
                          </select>
                        </td>
                        <td className="px-3 py-1.5 font-mono text-[11px] text-slate-700">{p.code}</td>
                        <td className="px-3 py-1.5">
                          <input
                            type="text"
                            value={p.address || ""}
                            onChange={(e) => setUnregisteredPdps((arr) => arr.map((x, i) => i === idx ? { ...x, address: e.target.value } : x))}
                            placeholder="Indirizzo (auto da PDF)"
                            className="w-full px-1.5 py-0.5 rounded border border-slate-200 text-[11px]"
                            data-testid={`unregistered-pdp-address-${p.code}`}
                          />
                        </td>
                        <td className="px-3 py-1.5 text-[11px] text-slate-500 truncate" style={{maxWidth: 200}} title={p.from_file}>{p.from_file || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-400 italic">
                Non vuoi aggiungerli? Chiudi questa finestra: nessun codice verrà censito.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/60">
              <button
                onClick={() => setUnregisteredPdps([])}
                className="text-sm px-3 py-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
                data-testid="unregistered-pdps-skip"
              >
                Non ora
              </button>
              <button
                onClick={async () => {
                  const toAdd = unregisteredPdps.filter((p) => p._checked);
                  let ok = 0;
                  for (const p of toAdd) {
                    try {
                      await api.post(`/companies/${selectedClient}/pdps`, {
                        code: p.code,
                        type: p.type,
                        address: (p.address || "").trim() || null,
                      });
                      ok += 1;
                    } catch (_e) { /* skip */ }
                  }
                  if (ok > 0) {
                    toast.success(`${ok} PdP aggiunti all'anagrafica di ${selClient?.name}`);
                    // Refresh companies state and company PdPs so il nuovo indirizzo si vede subito
                    try { await loadUsers(); } catch (_e) {}
                    try {
                      const { data: pdps } = await api.get(`/companies/${selectedClient}/pdps`);
                      setCompanyPdps(Array.isArray(pdps) ? pdps : []);
                    } catch (_e) {}
                  }
                  setUnregisteredPdps([]);
                }}
                className="text-sm px-4 py-1.5 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-700"
                data-testid="unregistered-pdps-confirm"
              >
                Aggiungi all'anagrafica
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
